import Processing from 'ext-ui/components/Processing/Processing'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ChatBox,
  ChatMarkdown,
  ChatWrapper,
  InputBox,
  LoadingText,
  MessageBox,
  MessageWrapper,
  Timestamp,
} from 'src/agent/components/AgentChat/AgentChat.style'
import AgentChatField from 'src/agent/components/AgentChatField/AgentChatField'
import StreamMessageRenderer from 'src/agent/components/StreamMessageRenderer/StreamMessageRenderer'
import { StreamEventType } from 'src/agent/types/streamingTypes'
import { useLoopingTypingEffect } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'

import {
  StreamMessage as HookStreamMessage,
  useStreamingAgentChat,
} from './useStreamingAgentChat'

export interface StreamAgentChatProps {
  chatKey?: string
}

interface UserMessage {
  id: string
  content: string
  timestamp: Date
}

interface CombinedMessage {
  id: string
  type: 'user' | 'stream'
  timestamp: Date
  userMessage?: UserMessage
  streamMessage?: HookStreamMessage
}

// Internal helper component to reduce repetition
interface ChatMessageProps {
  sender: 'user' | 'ai'
  timestamp: Date
  children: React.ReactNode
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  sender,
  timestamp,
  children,
}) => (
  <div>
    <MessageWrapper sender={sender}>
      <Timestamp>{timestamp.toLocaleString()}</Timestamp>
      <MessageBox sender={sender}>{children}</MessageBox>
    </MessageWrapper>
  </div>
)

const StreamAgentChat: React.FC<StreamAgentChatProps> = ({
  chatKey = 'default',
}) => {
  const translate = useTranslate()

  const chatBoxRef = useRef<HTMLDivElement>(null)
  const loadingRef = useRef<HTMLDivElement>(null)
  const [userInput, setUserInput] = useState<string>('')
  const [userMessages, setUserMessages] = useState<UserMessage[]>([])

  const {
    messages: streamMessages,
    isStreaming,
    error,
    sendPrompt,
  } = useStreamingAgentChat({
    threadId: undefined, // Will be set by the hook from response headers
  })

  // Combine user messages and stream messages in chronological order
  const combinedMessages = useMemo<CombinedMessage[]>(() => {
    const allMessages: CombinedMessage[] = []

    // Add user messages
    userMessages.forEach((msg) => {
      allMessages.push({
        id: msg.id,
        type: 'user',
        timestamp: msg.timestamp,
        userMessage: msg,
      })
    })

    // Add stream messages directly without complex linking
    streamMessages.forEach((msg) => {
      allMessages.push({
        id: msg.id,
        type: 'stream',
        timestamp: msg.timestamp,
        streamMessage: msg,
      })
    })

    // Sort by timestamp
    return allMessages.sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
    )
  }, [userMessages, streamMessages, isStreaming])

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    const scrollToBottom = () => {
      if (chatBoxRef.current) {
        chatBoxRef.current.scrollTo({
          behavior: 'smooth',
          top: chatBoxRef.current.scrollHeight,
        })
      }
    }

    const rafId = requestAnimationFrame(scrollToBottom)
    return () => cancelAnimationFrame(rafId)
  }, [combinedMessages, isStreaming])

  useEffect(() => {
    return () => {
      setUserInput('')
    }
  }, [])

  const loadingText = useLoopingTypingEffect([
    translate('Loading...'),
    translate('Working on it...'),
    translate('Just a moment...'),
    translate('Processing your request...'),
    translate('Thanks for your patience...'),
  ])

  const sendUserInput = (prompt: string): void => {
    if (prompt === '' || isStreaming) return

    // Add user message
    const userMessage: UserMessage = {
      id: `user-${Date.now()}-${Math.random()}`,
      content: prompt,
      timestamp: new Date(),
    }
    setUserMessages((prev) => [...prev, userMessage])
    setUserInput('')

    // Send prompt to streaming endpoint
    sendPrompt(prompt).catch(() => {
      // Error is already handled by the hook
    })
  }
  return (
    <ChatWrapper>
      <ChatBox ref={chatBoxRef}>
        {combinedMessages.length === 0 && !isStreaming && (
          <MessageWrapper sender="ai">
            <MessageBox sender="ai">
              <ChatMarkdown>
                {translate('How can I help you today?')}
              </ChatMarkdown>
            </MessageBox>
          </MessageWrapper>
        )}
        {combinedMessages.map((msg, idx) => {
          if (msg.type === 'user') {
            // Render user message
            return (
              <ChatMessage
                key={msg.id}
                sender="user"
                timestamp={msg.userMessage!.timestamp}
              >
                <ChatMarkdown>{msg.userMessage!.content}</ChatMarkdown>
              </ChatMessage>
            )
          } else {
            // Render stream message
            const streamMsg = msg.streamMessage!

            // For messages without eventType (fallback messages), render as plain markdown
            if (!streamMsg.eventType) {
              return (
                <ChatMessage
                  key={msg.id}
                  sender="ai"
                  timestamp={streamMsg.timestamp}
                >
                  <ChatMarkdown>{streamMsg.content || ''}</ChatMarkdown>
                </ChatMessage>
              )
            }

            // Convert hook message to renderer message format
            const rendererMessage = {
              id: streamMsg.id,
              timestamp: streamMsg.timestamp,
              eventType: streamMsg.eventType,
              content: streamMsg.content,
              toolCalls: streamMsg.toolCalls,
            }

            if (rendererMessage.eventType === StreamEventType.DONE) {
              return <></>
            }

            if (
              rendererMessage.eventType === StreamEventType.TOOL_EVENT &&
              rendererMessage.content?.match(/^Updated todo list to \[\{/i)
            ) {
              return <></>
            }

            // Check if this is the last stream message (for showing "Running Task" vs "Task Complete")
            const isLatestMessage =
              idx === combinedMessages.length - 1 && isStreaming

            return (
              <ChatMessage
                key={msg.id}
                sender="ai"
                timestamp={streamMsg.timestamp}
              >
                <StreamMessageRenderer
                  message={rendererMessage}
                  isLatestMessage={isLatestMessage}
                />
              </ChatMessage>
            )
          }
        })}
        {isStreaming && (
          <LoadingText ref={loadingRef}>
            <Processing /> {loadingText}
          </LoadingText>
        )}
        {error && (
          <MessageWrapper sender="ai">
            <MessageBox sender="ai">
              <ChatMarkdown>
                {translate(
                  'Sorry, something went wrong. Please try again later.',
                )}
              </ChatMarkdown>
            </MessageBox>
          </MessageWrapper>
        )}
      </ChatBox>
      <InputBox>
        <AgentChatField
          value={userInput}
          options={undefined} // Stream chat doesn't support interrupt options
          onOptionClick={() => {}} // Not used in stream chat
          suggestedPrompts={undefined} // Could be added later if needed
          onSuggestedPromptClick={() => {}} // Not used in stream chat
          onChange={(value): void => setUserInput(value)}
          onSubmit={(): void => sendUserInput(userInput)}
          placeholder={translate('Enter your request')}
          disabled={isStreaming}
          loading={isStreaming}
        />
      </InputBox>
    </ChatWrapper>
  )
}

export default StreamAgentChat
